import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { HomeIcon, ArrowTopRightOnSquareIcon, ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchAgenciesSnapshot } from '../utils/agenciesPublic';

/** Strip scheme for user-facing redirect messages (e.g. https://davistate.com → davistate.com). */
function formatUrlForDisplay(url) {
  if (!url) return '';
  return String(url).trim().replace(/^https?:\/\//i, '');
}

/** Agency homepage host only (e.g. www.davistate.com), not a full listing path. */
function homepageDisplayUrl(homepage, utmSource, listingUrl) {
  const raw = homepage
    || (utmSource ? `https://${String(utmSource).replace(/^https?:\/\//i, '')}` : '')
    || listingUrl
    || '';
  if (!raw) return '';
  try {
    const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(normalized).host;
  } catch {
    return formatUrlForDisplay(raw).split('/')[0];
  }
}

function Home() {
  const { location, loading: locationLoading, error: locationError, servicesDisabled, requestLocation } = useLocation();
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [propertiesError, setPropertiesError] = useState(null);
  const [propertyRecords, setPropertyRecords] = useState({});
  const [utmSource, setUtmSource] = useState(null);
  const [utmSourceValidated, setUtmSourceValidated] = useState(false); // Track if utm_source has been validated
  const [filteredProperties, setFilteredProperties] = useState(null);
  const [filteringProperties, setFilteringProperties] = useState(false);
  /** UTM scan: hide property list until auto-redirect fails (then show manual fallback). */
  const [utmAwaitingAutoRoute, setUtmAwaitingAutoRoute] = useState(false);
  /** { kind: 'listing' | 'homepage', url: display host/path without https:// } */
  const [utmRedirectStatus, setUtmRedirectStatus] = useState(null);
  
  // Phase 3: Optimized loading states
  const [loadingProgress, setLoadingProgress] = useState({ stage: '', progress: 0 });
  // Removed autoLinkUrl and showAutoLinkNotification as we now navigate directly
  const [multipleMatches, setMultipleMatches] = useState([]);
  const [showMultipleMatches, setShowMultipleMatches] = useState(false);
  const [clickedProperties, setClickedProperties] = useState(new Set());
  const [utmFallbackUrl, setUtmFallbackUrl] = useState(null);
  const [utmFallbackAgencyName, setUtmFallbackAgencyName] = useState(null);
  const [utmFallbackBaseUrl, setUtmFallbackBaseUrl] = useState(null);
  const openedUrlsRef = useRef(new Set());
  const trackedInteractionsRef = useRef(new Set());
  const hasNavigatedRef = useRef(false); // Prevent duplicate navigation tracking per session
  const trackingInProgressRef = useRef(false); // Global lock for tracking
  const lastTrackTimeRef = useRef(0); // Timestamp of last track call
  
  // Phase 3: Caching strategy for better performance
  const [propertyRecordsCache, setPropertyRecordsCache] = useState(new Map());
  const propertyRecordsCacheRef = useRef(propertyRecordsCache);
  const nearbyFetchGenRef = useRef(0);
  const agencyMatchRecordsKeyRef = useRef('');
  /** Set when utm_source is validated (avoids re-fetching all agencies/records on scan). */
  const validatedAgencyRef = useRef(null);
  const lastResumeRefreshRef = useRef(0);
  const hasBeenHiddenRef = useRef(false);
  const [utmFallbackCache, setUtmFallbackCache] = useState(new Map());

  /** Min interval between background→foreground refresh (avoids tab-switch spam). */
  const RESUME_REFRESH_MIN_MS = 30_000;

  useEffect(() => {
    propertyRecordsCacheRef.current = propertyRecordsCache;
  }, [propertyRecordsCache]);
  
  // Helper function to safely open URLs (prevents duplicates)
  const safeOpenUrl = useCallback((url) => {
    if (openedUrlsRef.current.has(url)) {
      return false; // Already opened
    }
    openedUrlsRef.current.add(url);
    window.location.href = url;
    return true; // Successfully navigated
  }, []);

  // Helper function to safely track interactions (prevents duplicates)
  const safeTrackInteraction = useCallback(async (interactionType, uprn, agentName, agentListingLink, address, propertyId = null, branch = null) => {
    const now = Date.now();
    
    // TIMESTAMP CHECK: Block if called within 100ms of last call
    if (now - lastTrackTimeRef.current < 100) {
      return Promise.resolve(false);
    }
    
    // Update last track time IMMEDIATELY
    lastTrackTimeRef.current = now;
    
    // GLOBAL LOCK: If any tracking is in progress, block all other calls
    if (trackingInProgressRef.current) {
      return Promise.resolve(false);
    }
    
    // Create unique key for this interaction
    const interactionKey = `${interactionType}-${uprn}-${agentName}`;
    
    // Synchronously check and add to prevent race conditions
    if (trackedInteractionsRef.current.has(interactionKey)) {
      return Promise.resolve(false); // Already tracked
    }
    
    // Set global lock IMMEDIATELY
    trackingInProgressRef.current = true;
    
    // Immediately add to ref to block other simultaneous calls
    trackedInteractionsRef.current.add(interactionKey);
    
    // Track asynchronously and return promise
    try {
      const finalAddress = (address && address.trim() !== '') ? address : `UPRN: ${uprn}`;
      
      const interactionData = {
        timestamp: serverTimestamp(),
        interactionType: interactionType,
        propertyUprn: uprn,
        propertyAddress: finalAddress,
        utmSource: utmSource,
        agentName: agentName,
        agentListingLink: agentListingLink,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        currentUrl: window.location.href
      };
      
      // Include propertyId if provided (even if empty string, to preserve the field)
      if (propertyId !== null && propertyId !== undefined) {
        interactionData.propertyId = String(propertyId).trim();
      }
      
      // Include branch if provided
      if (branch !== null && branch !== undefined) {
        interactionData.branch = String(branch).trim();
      }
      
      await addDoc(collection(db, 'propertyInteractions'), interactionData);
      return true; // Tracking completed successfully
    } catch (error) {
      console.error('Error tracking interaction:', error);
      // Remove from set if tracking failed so it can be retried
      trackedInteractionsRef.current.delete(interactionKey);
      return false; // Tracking failed
    } finally {
      // Release global lock
      trackingInProgressRef.current = false;
    }
  }, [utmSource]);

  /** Auto-route to agent listing (scan.qr.properties UTM flow). */
  const navigateToAgentListing = useCallback(async (property, record, interactionType = 'agent_listing_click') => {
    if (!record?.agentListingLink) return false;

    const baseUrl = record.agentListingLink;
    const separator = baseUrl.includes('?') ? '&' : '?';
    const utmUrl = `${baseUrl}${separator}utm_source=qr.properties`;
    if (hasNavigatedRef.current || openedUrlsRef.current.has(utmUrl)) return false;

    const address = property?.single_line_address || property?.address
      || record.addressFirstLine || `UPRN: ${property?.uprn || utmSource}`;

    try {
      await safeTrackInteraction(
        interactionType,
        property?.uprn || utmSource,
        record.agent,
        record.agentListingLink,
        address,
        record.propertyId,
        record.branch
      );
    } catch (trackErr) {
      console.warn('Track before auto-navigation failed:', trackErr);
    }

    openedUrlsRef.current.add(utmUrl);
    hasNavigatedRef.current = true;
    window.location.replace(utmUrl);
    return true;
  }, [utmSource, safeTrackInteraction]);

  // Phase 3: Memory optimization - cache cleanup
  const cleanupCache = useCallback(() => {
    // Limit cache size to prevent memory bloat
    const maxCacheSize = 100;
    
    setPropertyRecordsCache(prev => {
      if (prev.size > maxCacheSize) {
        const entries = Array.from(prev.entries());
        const recentEntries = entries.slice(-maxCacheSize);
        return new Map(recentEntries);
      }
      return prev;
    });
    
    setUtmFallbackCache(prev => {
      if (prev.size > maxCacheSize) {
        const entries = Array.from(prev.entries());
        const recentEntries = entries.slice(-maxCacheSize);
        return new Map(recentEntries);
      }
      return prev;
    });
  }, []);

  // Helper function to check if utm_source matches agencyId (exact match or prefix match)
  const matchesAgencyId = useCallback((utmSource, agencyId) => {
    if (!agencyId || !utmSource) return false;
    
    const utmSourceLower = utmSource.trim().toLowerCase();
    const agencyIdLower = agencyId.trim().toLowerCase();
    
    // Exact match (case-insensitive)
    if (agencyIdLower === utmSourceLower) {
      return true;
    }
    
    // Prefix match: check if agencyId starts with utm_source followed by a delimiter (e.g., "WNK" matches "WNK-GRNE")
    // Common delimiters: -, _, space, or end of string
    const prefixPattern = new RegExp(`^${utmSourceLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([-_\\s]|$)`);
    return prefixPattern.test(agencyIdLower);
  }, []);

  // STEP 1: Parse UTM parameters and validate against agencies collection FIRST (before location checking)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSourceParam = urlParams.get('utm_source');
    if (utmSourceParam) {
      // Validate utm_source against agencies collection BEFORE proceeding with any location checking
      const validateAgency = async () => {
        try {
          // Check if utm_source matches an agencyId via public agency directory
          const agenciesSnapshot = await fetchAgenciesSnapshot();
          
          let agencyFound = false;
          
          // Check if agencyId matches (exact or prefix match)
          for (const doc of agenciesSnapshot.docs) {
            const agencyData = doc.data();
            const agencyId = agencyData.agencyId ? agencyData.agencyId.trim() : '';
            
            if (agencyId && matchesAgencyId(utmSourceParam, agencyId)) {
              agencyFound = true;
              validatedAgencyRef.current = {
                agencyId,
                homepage: agencyData.homepage || null,
              };
              break; // Found match, no need to check further
            }
          }
          
          // If no agency match found, redirect immediately to visi.properties
          // Don't check location, don't track event, don't proceed with any other logic
          if (!agencyFound) {
            const visiUrl = 'https://visi.properties';
            // Redirect immediately without setTimeout to prevent any other code from running
            window.location.replace(visiUrl);
            return;
          }
          
          // Agency found - proceed with normal flow
          // Reset navigation flag when utm_source changes (allows tracking different agencies in same session)
          hasNavigatedRef.current = false;
          openedUrlsRef.current.clear();
          setUtmAwaitingAutoRoute(true);
          setUtmRedirectStatus(null);
          setUtmSource(utmSourceParam);
          setUtmSourceValidated(true); // Mark as validated
        } catch (error) {
          console.error('Error validating agency:', error);
          // On error, redirect to visi.properties immediately
          const visiUrl = 'https://visi.properties';
          window.location.replace(visiUrl);
        }
      };
      
      validateAgency();
    }
  }, [matchesAgencyId]);

  // Helper function to construct fallback URL from utm_source (base URL without UTM parameter)
  const constructFallbackBaseUrl = useCallback((source) => {
    // Remove protocol if present
    let cleanSource = source.replace(/^https?:\/\//, '');
    // Construct base URL with https:// prefix (without UTM parameter)
    return `https://${cleanSource}`;
  }, []);

  // Helper function to construct fallback URL with UTM parameter for navigation
  const constructFallbackUrl = useCallback((source) => {
    const baseUrl = constructFallbackBaseUrl(source);
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}utm_source=qr.properties`;
  }, [constructFallbackBaseUrl]);

  // Phase 3: Cached UTM fallback URL fetch (only runs after utmSource is validated)
  useEffect(() => {
    if (utmSource && utmSourceValidated) {
      const fetchUtmFallbackUrl = async () => {
        try {
          // Re-validate against public agency directory (in case agency was deleted)
          const agenciesSnapshot = await fetchAgenciesSnapshot();
          
          let agencyFound = false;
          let agencyHomepage = null;
          let actualAgencyName = utmSource;
          
          agenciesSnapshot.docs.forEach(doc => {
            const agencyData = doc.data();
            const agencyId = agencyData.agencyId;
            
            if (agencyId && matchesAgencyId(utmSource, agencyId)) {
              agencyFound = true;
              actualAgencyName = agencyId;
              agencyHomepage = agencyData.homepage || null;
            }
          });
          
          // If agency not found, don't set fallback URL
          if (!agencyFound) {
            setUtmFallbackUrl(null);
            setUtmFallbackAgencyName(null);
            setUtmFallbackBaseUrl(null);
            return;
          }
          
          // Check cache first
          if (utmFallbackCache.has(utmSource)) {
            setUtmFallbackUrl(utmFallbackCache.get(utmSource));
            // Also set agency name and base URL for tracking
            setUtmFallbackAgencyName(actualAgencyName);
            // Extract base URL from cached URL (remove UTM parameter)
            const cachedUrl = utmFallbackCache.get(utmSource);
            // Remove UTM parameter from URL for tracking
            try {
              const urlObj = new URL(cachedUrl);
              urlObj.searchParams.delete('utm_source');
              const baseUrl = urlObj.toString();
              setUtmFallbackBaseUrl(baseUrl);
            } catch (e) {
              // Fallback: simple string manipulation if URL parsing fails
              const baseUrl = cachedUrl.replace(/[?&]utm_source=qr\.properties/g, '').replace(/[?&]$/, '');
              setUtmFallbackBaseUrl(baseUrl);
            }
            return;
          }
          
          const uprnString = utmSource;
          let utmUrl = null;
          let baseUrlForTracking = null;

          const { fetchListingsByUprn, fetchAgencyListingLinkSample, pickPrimaryListingRow, rowToLegacyRecord } =
            await import('../utils/listingsLookup');

          const uprnRows = await fetchListingsByUprn(uprnString);
          let record = rowToLegacyRecord(pickPrimaryListingRow(uprnRows, actualAgencyName, utmSource, matchesAgencyId));

          if (!record?.agentListingLink) {
            const sample = await fetchAgencyListingLinkSample(actualAgencyName);
            record = rowToLegacyRecord(sample);
          }

          if (record?.agent && record?.agentListingLink) {
            baseUrlForTracking = record.agentListingLink;
            const separator = baseUrlForTracking.includes('?') ? '&' : '?';
            utmUrl = `${baseUrlForTracking}${separator}utm_source=qr.properties`;
          }
          
          // If no UPRN match found, use homepage from agencies collection
          if (!utmUrl) {
            if (agencyHomepage) {
              baseUrlForTracking = agencyHomepage;
              const separator = agencyHomepage.includes('?') ? '&' : '?';
              utmUrl = `${agencyHomepage}${separator}utm_source=qr.properties`;
            } else {
              // Fallback to visi.properties if no homepage
              baseUrlForTracking = 'https://visi.properties';
              utmUrl = 'https://visi.properties?utm_source=qr.properties';
            }
          }
          
          // Cache and set the result
          if (utmUrl) {
            setUtmFallbackCache(prev => new Map(prev).set(utmSource, utmUrl));
            setUtmFallbackUrl(utmUrl);
            setUtmFallbackAgencyName(actualAgencyName);
            setUtmFallbackBaseUrl(baseUrlForTracking);
          }
        } catch (error) {
          console.error('Error fetching UTM fallback URL:', error);
        }
      };
      
      fetchUtmFallbackUrl();
    }
  }, [utmSource, utmSourceValidated, utmFallbackCache, constructFallbackUrl, matchesAgencyId]);



  // Check for UTM source fallback when location is denied
  useEffect(() => {
    if (utmSource && utmSourceValidated && locationError) {
      // User denied location but has UTM source - validate against agencies collection first
      const checkUtmSourceFallback = async () => {
        try {
          // STEP 1: Validate against public agency directory
          const agenciesSnapshot = await fetchAgenciesSnapshot();
          
          let agencyFound = false;
          let actualAgencyName = utmSource;
          let agencyHomepage = null;
          
          agenciesSnapshot.docs.forEach(doc => {
            const agencyData = doc.data();
            const agencyId = agencyData.agencyId;
            
            if (agencyId && matchesAgencyId(utmSource, agencyId)) {
              agencyFound = true;
              actualAgencyName = agencyId;
              agencyHomepage = agencyData.homepage || null;
            }
          });
          
          // If agency not found in agencies collection, redirect immediately
          if (!agencyFound) {
            const visiUrl = 'https://visi.properties';
            setProperties([]);
            setFilteredProperties([]);
            // Redirect immediately without setTimeout
            window.location.replace(visiUrl);
            return;
          }
          
          const uprnString = utmSource;
          const { fetchListingsByUprn, fetchAgencyListingLinkSample, pickPrimaryListingRow, rowToLegacyRecord } =
            await import('../utils/listingsLookup');

          const uprnRows = await fetchListingsByUprn(uprnString);
          let record = rowToLegacyRecord(pickPrimaryListingRow(uprnRows, actualAgencyName, utmSource, matchesAgencyId));

          if (!record) {
            const sample = await fetchAgencyListingLinkSample(actualAgencyName);
            record = rowToLegacyRecord(sample);
          }
          
          let utmUrl = null;
          
          if (record && record.agent && record.agentListingLink) {
            // Use record if agent name matches agencyId from agencies collection (exact or prefix match)
            const agentMatches = record.agent.toLowerCase() === actualAgencyName.toLowerCase() || 
                                 matchesAgencyId(utmSource, record.agent);
            if (agentMatches) {
              const baseUrl = record.agentListingLink;
              const separator = baseUrl.includes('?') ? '&' : '?';
              utmUrl = `${baseUrl}${separator}utm_source=qr.properties`;
              
              // Track automatic navigation (location denied)
              await safeTrackInteraction(
                'agent_listing_click',
                record.uprn || uprnString,
                record.agent,
                record.agentListingLink,
                record.addressFirstLine || `UPRN: ${record.uprn || uprnString}`,
                record.propertyId,
                record.branch
              );
            }
          }
          
          // If no exact match found, use homepage from agencies collection
          if (!utmUrl) {
            let fallbackBaseUrl;
            let fallbackUrl;
            
            if (agencyHomepage) {
              // Use homepage from agencies collection
              fallbackBaseUrl = agencyHomepage;
              const separator = agencyHomepage.includes('?') ? '&' : '?';
              fallbackUrl = `${agencyHomepage}${separator}utm_source=qr.properties`;
            } else {
              // No homepage - use visi.properties
              fallbackBaseUrl = 'https://visi.properties';
              fallbackUrl = 'https://visi.properties?utm_source=qr.properties';
            }
            
            utmUrl = fallbackUrl;
            
            // Track automatic navigation (location denied, no UPRN match)
            // Store base URL without UTM parameter, but navigate to URL with UTM parameter
            await safeTrackInteraction(
              'homepage_link',
              uprnString,
              utmSource,
              fallbackBaseUrl,
              `Agent: ${utmSource}`,
              null,
              null
            );
          }
          
          // Navigate to the UTM fallback URL
          if (utmUrl) {
            // Clear properties to prevent showing nearby properties when using fallback
            setProperties([]);
            setFilteredProperties([]);
            setTimeout(() => {
              safeOpenUrl(utmUrl);
            }, 100);
          }
        } catch (error) {
          console.error('Error in UTM source fallback check:', error);
        }
      };
      
      checkUtmSourceFallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utmSource, locationError, safeOpenUrl, constructFallbackUrl, matchesAgencyId]);

  const searchForAgentProperties = useCallback(async (properties, recordsMap = {}) => {
    if (!utmSource || !properties || properties.length === 0) {
      return null;
    }

    setUtmAwaitingAutoRoute(true);
    setFilteringProperties(true);

    try {
      // Use agency from initial utm validation (skip full agencies + propertyRecords scans).
      let actualAgencyName = utmSource;
      let agencyHomepage = null;
      const cachedAgency = validatedAgencyRef.current;

      if (cachedAgency?.agencyId) {
        actualAgencyName = cachedAgency.agencyId;
        agencyHomepage = cachedAgency.homepage;
      } else {
        const agenciesSnapshot = await fetchAgenciesSnapshot();
        let agencyFound = false;
        agenciesSnapshot.docs.forEach((doc) => {
          const agencyData = doc.data();
          const agencyId = agencyData.agencyId;
          if (agencyId && matchesAgencyId(utmSource, agencyId)) {
            agencyFound = true;
            actualAgencyName = agencyId;
            agencyHomepage = agencyData.homepage || null;
            validatedAgencyRef.current = { agencyId, homepage: agencyHomepage };
          }
        });
        if (!agencyFound) {
          const visiUrl = 'https://visi.properties';
          setProperties([]);
          setFilteredProperties([]);
          setFilteringProperties(false);
          setUtmAwaitingAutoRoute(false);
          window.location.replace(visiUrl);
          return;
        }
      }

      // Agent aliases from nearby records only (not entire propertyRecords collection).
      const matchingAgentNames = new Set();
      const agencyIdLower = actualAgencyName.toLowerCase();
      Object.values(recordsMap).forEach((record) => {
        const agent = record?.agent;
        if (!agent) return;
        if (agent.toLowerCase() === agencyIdLower || matchesAgencyId(utmSource, agent)) {
          matchingAgentNames.add(agent);
        }
      });

      setFilteringProperties(false);

      // Check nearby UPRNs for matches
      const matchingProperties = [];
      
      // Single loop through all properties
      for (const property of properties) {
        if (!property.uprn) continue;
        
        // Use pre-loaded record from batch query
        const record = recordsMap[String(property.uprn)];
        
        if (record && record.agent) {
          const agent = record.agent;
          
          // Check if agent name matches any of the matching agent names (case-insensitive)
          const matchesAgentNames = matchingAgentNames.size > 0 
            ? Array.from(matchingAgentNames).some(name => name.toLowerCase() === agent.toLowerCase())
            : false;
          
          // Check if agent name matches utm_source prefix pattern (e.g., utm_source="wnk" matches agent="WNK-GRNE")
          const matchesPrefix = matchesAgencyId(utmSource, agent);
          
          // Check if agent name exactly matches actualAgencyName (the matched agencyId from agencies collection)
          const matchesExact = agent.toLowerCase() === actualAgencyName.toLowerCase();
          
          // Match if any of the three conditions are true
          const isMatch = matchesAgentNames || matchesPrefix || matchesExact;
          
          if (isMatch) {
            matchingProperties.push({
              property,
              record,
              distance: property.distance || 0
            });
          }
        }
      }
      
      // Sort by distance (proximity) for consistent ordering
      matchingProperties.sort((a, b) => a.distance - b.distance);
      
      // STEP 4: Handle results based on match count
      if (matchingProperties.length > 1) {
        // Multiple matches - show selection interface
        setMultipleMatches(matchingProperties);
        setShowMultipleMatches(true);
        setFilteredProperties([]);
        setFilteringProperties(false);
        setUtmAwaitingAutoRoute(false);
        setUtmRedirectStatus(null);
        return;
      }
      
      if (matchingProperties.length === 1) {
        const { property, record } = matchingProperties[0];

        let navigated = false;
        if (record.agentListingLink) {
          setUtmRedirectStatus({
            kind: 'listing',
            url: homepageDisplayUrl(agencyHomepage, utmSource, record.agentListingLink),
          });
          navigated = await navigateToAgentListing(property, record);
        }
        if (!navigated) {
          setFilteredProperties([property]);
          setUtmAwaitingAutoRoute(false);
          setUtmRedirectStatus(null);
        }
        return;
      }
      
      // STEP 4: No UPRN matches found - use homepage from agencies collection
      let fallbackBaseUrl;
      let fallbackUrl;
      
      if (agencyHomepage) {
        // Use homepage from agencies collection
        fallbackBaseUrl = agencyHomepage;
        const separator = agencyHomepage.includes('?') ? '&' : '?';
        fallbackUrl = `${agencyHomepage}${separator}utm_source=qr.properties`;
      } else {
        // No homepage - use visi.properties
        fallbackBaseUrl = 'https://visi.properties';
        fallbackUrl = 'https://visi.properties?utm_source=qr.properties';
      }
      
      // Store base URL without UTM parameter, but navigate to URL with UTM parameter
      // Use actualAgencyName from database (preserves case) instead of utmSource
      if (!hasNavigatedRef.current && !openedUrlsRef.current.has(fallbackUrl)) {
        setUtmRedirectStatus({
          kind: 'homepage',
          url: homepageDisplayUrl(fallbackBaseUrl, utmSource, null),
        });
        try {
          await safeTrackInteraction(
            'homepage_link',
            utmSource,
            actualAgencyName,
            fallbackBaseUrl,
            `Agent: ${actualAgencyName}`,
            null,
            null
          );
        } catch (trackErr) {
          console.warn('Homepage fallback track failed:', trackErr);
        }
        openedUrlsRef.current.add(fallbackUrl);
        hasNavigatedRef.current = true;
        setProperties([]);
        setFilteredProperties([]);
        setFilteringProperties(false);
        window.location.replace(fallbackUrl);
        return;
      }

      setFilteringProperties(false);
      setUtmAwaitingAutoRoute(false);
      setUtmRedirectStatus(null);
      return;
      
    } catch (error) {
      console.error('Error searching for agent properties:', error);
      // On error, route to visi.properties without tracking
      const visiUrl = 'https://visi.properties';
      setProperties([]);
      setFilteredProperties([]);
      setFilteringProperties(false);
      setUtmAwaitingAutoRoute(false);
      setUtmRedirectStatus(null);
      // Redirect immediately without setTimeout
      window.location.replace(visiUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utmSource, safeOpenUrl, constructFallbackUrl, matchesAgencyId, navigateToAgentListing]);

  const searchForAgentPropertiesRef = useRef(searchForAgentProperties);
  useEffect(() => {
    searchForAgentPropertiesRef.current = searchForAgentProperties;
  }, [searchForAgentProperties]);

  // Re-run agency match when Firestore records arrive after nearby list (e.g. slow first load).
  useEffect(() => {
    if (!utmSource || !properties?.length || hasNavigatedRef.current || filteringProperties) return;
    const recordsKey = Object.keys(propertyRecords).sort().join(',');
    if (!recordsKey || recordsKey === agencyMatchRecordsKeyRef.current) return;
    agencyMatchRecordsKeyRef.current = recordsKey;
    void searchForAgentPropertiesRef.current(properties, propertyRecords);
  }, [utmSource, properties, propertyRecords, filteringProperties]);

  const loadPropertyRecordsForUprns = useCallback(async (uniqueUprns, fetchGen) => {
    const recordsMap = {};
    const missingUprns = [];
    const cache = propertyRecordsCacheRef.current;

    uniqueUprns.forEach((uprn) => {
      if (cache.has(uprn)) {
        recordsMap[uprn] = cache.get(uprn);
      } else {
        missingUprns.push(uprn);
      }
    });

    if (missingUprns.length === 0) return recordsMap;

    const { fetchListingsByUprn, rowToLegacyRecord } = await import('../utils/listingsLookup');
    const TIMEOUT_MS = 15000;

    const loadPromise = Promise.all(
      missingUprns.map(async (uprn) => {
        const rows = await fetchListingsByUprn(uprn);
        const primary = rows[0];
        return primary ? { uprn, data: rowToLegacyRecord(primary) } : null;
      })
    );

    const loaded = await Promise.race([
      loadPromise,
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Property listings request timed out')), TIMEOUT_MS);
      }),
    ]);

    if (fetchGen !== nearbyFetchGenRef.current) return null;

    const nextCache = new Map(propertyRecordsCacheRef.current);
    loaded.forEach((item) => {
      if (!item?.data) return;
      recordsMap[item.uprn] = item.data;
      nextCache.set(item.uprn, item.data);
    });

    propertyRecordsCacheRef.current = nextCache;
    setPropertyRecordsCache(nextCache);

    return recordsMap;
  }, []);

  const fetchNearbyProperties = useCallback(async (latitude, longitude) => {
    try {
      const fetchGen = ++nearbyFetchGenRef.current;
      openedUrlsRef.current.clear();
      hasNavigatedRef.current = false;
      agencyMatchRecordsKeyRef.current = '';
      if (utmSource) {
        setUtmAwaitingAutoRoute(true);
        setUtmRedirectStatus(null);
      }
      setLoadingProperties(true);
      setPropertiesError(null);
      setLoadingProgress({ stage: 'Fetching nearby properties...', progress: 20 });
      
      const response = await fetch(
        `${
          process.env.REACT_APP_FUNCTIONS_URL ||
          'https://europe-west2-qrproperties.cloudfunctions.net'
        }/getNearestUprns`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lon: longitude,
            lat: latitude,
            result_limit: 10,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Limit to first 10 properties
      const limitedData = data.slice(0, 10);
      
      setProperties(limitedData);
      // Unblock UI immediately; Firestore records + agent matching can load in background.
      setLoadingProperties(false);
      setLoadingProgress({ stage: '', progress: 0 });
      
      if (!limitedData?.length) return;

      const uniqueUprns = [...new Set(limitedData.map((p) => String(p.uprn)).filter(Boolean))];

      let recordsMap = {};
      try {
        const loaded = await loadPropertyRecordsForUprns(uniqueUprns, fetchGen);
        if (loaded) recordsMap = loaded;
      } catch (recordsError) {
        console.warn('Property records load failed:', recordsError);
        // Best-effort: use whatever is in cache right now.
        uniqueUprns.forEach((uprn) => {
          if (propertyRecordsCacheRef.current.has(uprn)) {
            recordsMap[uprn] = propertyRecordsCacheRef.current.get(uprn);
          }
        });
      }

      if (fetchGen !== nearbyFetchGenRef.current) return;

      setPropertyRecords(recordsMap);
      cleanupCache();

      if (utmSource) {
        const recordsKey = Object.keys(recordsMap).sort().join(',');
        if (recordsKey) agencyMatchRecordsKeyRef.current = recordsKey;
        await searchForAgentProperties(limitedData, recordsMap);
      }
    } catch (error) {
      console.error('Error fetching nearby properties:', error);
      setPropertiesError('Failed to fetch nearby properties');
    } finally {
      setLoadingProperties(false);
      setLoadingProgress({ stage: '', progress: 0 });
    }
  }, [utmSource, cleanupCache, loadPropertyRecordsForUprns, searchForAgentProperties]);

  // lookupPropertyRecord function removed - now using batch queries for better performance

  const handleMultipleMatchClick = async (match) => {
    const { property, record } = match;
    
    // Mark this property as clicked (single selection only)
    setClickedProperties(new Set([property.uprn]));
    
    // Track multiple match selection (includes listing link)
    const address = property.single_line_address || property.address || record.addressFirstLine || `UPRN: ${property.uprn}`;
    // Ensure propertyId is passed correctly (handle undefined, null, or empty string)
    const propertyId = record.propertyId !== undefined && record.propertyId !== null ? String(record.propertyId).trim() : null;
    // Ensure branch is passed correctly
    const branch = record.branch !== undefined && record.branch !== null ? String(record.branch).trim() : null;
    safeTrackInteraction('multiple_match_click', property.uprn, record.agent, record.agentListingLink, address, propertyId, branch);

    if (record.agentListingLink) {
      // Append UTM source to the agent listing URL
      const baseUrl = record.agentListingLink;
      const separator = baseUrl.includes('?') ? '&' : '?';
      const utmUrl = `${baseUrl}${separator}utm_source=qr.properties`;
      
      // Note: We don't track agent_listing_click here because multiple_match_click already captures this interaction
      // Tracking both would be redundant and triggers the timestamp blocker
      
      // Navigate to agent listing
      window.location.href = utmUrl;
    } else {
      alert('No agent listing link found for this property');
    }
  };

  // Removed trackPropertyInteraction - now using safeTrackInteraction everywhere for deduplication

  const fetchNearbyPropertiesRef = useRef(fetchNearbyProperties);
  useEffect(() => {
    fetchNearbyPropertiesRef.current = fetchNearbyProperties;
  }, [fetchNearbyProperties]);

  useEffect(() => {
    if (location?.latitude != null && location?.longitude != null && !servicesDisabled) {
      fetchNearbyPropertiesRef.current(location.latitude, location.longitude);
    }
  }, [location?.latitude, location?.longitude, servicesDisabled]);

  const refreshNearbyFromCurrentGps = useCallback(async () => {
    const loc = await requestLocation();
    if (loc?.latitude != null && loc?.longitude != null && !servicesDisabled) {
      fetchNearbyPropertiesRef.current(loc.latitude, loc.longitude);
    }
  }, [servicesDisabled, requestLocation]);

  const handleManualRefreshNearby = useCallback(async () => {
    if (servicesDisabled || utmSource) return;
    await refreshNearbyFromCurrentGps();
  }, [servicesDisabled, utmSource, refreshNearbyFromCurrentGps]);

  const refreshLocationAndNearby = useCallback(async () => {
    if (servicesDisabled || utmSource) return;

    const now = Date.now();
    if (now - lastResumeRefreshRef.current < RESUME_REFRESH_MIN_MS) return;
    lastResumeRefreshRef.current = now;

    await refreshNearbyFromCurrentGps();
  }, [servicesDisabled, utmSource, refreshNearbyFromCurrentGps]);

  // When user returns from another app/tab, refresh GPS + nearby list (if they moved).
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hasBeenHiddenRef.current = true;
        return;
      }
      if (document.visibilityState === 'visible' && hasBeenHiddenRef.current) {
        refreshLocationAndNearby();
      }
    };

    const onPageShow = (event) => {
      if (event.persisted) refreshLocationAndNearby();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [refreshLocationAndNearby]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Title Banner */}
      <div className="text-white" style={{ backgroundColor: '#356C9C' }}>
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              QR.Properties
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Auto-Link Notification removed - now navigating directly */}

        {/* Multiple Matches Notification */}
        {showMultipleMatches && multipleMatches.length > 0 && (
          <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Multiple properties found
              </h3>
              <p className="text-blue-700">
                We found {multipleMatches.length} properties nearby. Please select the one you're interested in:
              </p>
            </div>
            <div className="space-y-3">
              {multipleMatches.map((match, index) => {
                const { property, record } = match;
                const distance = property.distance ? `${(property.distance * 1000).toFixed(0)}m away` : '';
                
                return (
                  <div 
                    key={`${property.uprn}-${index}`}
                    className="p-4 bg-white border border-blue-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer active:bg-blue-50 touch-manipulation"
                    onClick={() => handleMultipleMatchClick(match)}
                    onTouchStart={() => {}} // Ensure touch events are captured
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleMultipleMatchClick(match);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {record.addressFirstLine || property.single_line_address || property.address}
                        </div>
                        {distance && (
                          <div className="text-sm text-gray-600">
                            {distance}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        {clickedProperties.has(property.uprn) ? (
                          <CheckIcon className="h-5 w-5 text-green-600" />
                        ) : (
                          <ArrowTopRightOnSquareIcon className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Location Loading State */}
        {locationLoading && (
          <div className="bg-white shadow rounded-lg p-8 mb-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3"></div>
              <span className="text-lg text-gray-600">Getting your location...</span>
            </div>
          </div>
        )}
        
        {/* Location Error State */}
        {locationError && (
          <div className="bg-white shadow rounded-lg p-8 mb-8">
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-lg text-red-800 mb-4">QR.Properties would like to use your location to link directly to property details.</p>
              <button
                onClick={requestLocation}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
              >
                Provide option again
              </button>
            </div>
          </div>
        )}

        {/* Location Services Disabled State */}
        {servicesDisabled && !locationLoading && (
          <div className="bg-white shadow rounded-lg p-8 mb-8">
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Location Services Disabled
              </h3>
              <p className="text-blue-700 mb-4">
                QR.Properties needs your location to find nearby properties and link to the details.<br />
                If you've enabled location services, try the button below to retry.
              </p>
              <div className="flex flex-col gap-3 items-center">
                <button
                  onClick={requestLocation}
                  className="px-4 py-3 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors w-full max-w-md"
                >
                  Retry Location Services
                </button>
                {utmSource && (
                  <button
                    onClick={async () => {
                      // Track homepage_link event before navigating
                      if (utmFallbackUrl && utmFallbackAgencyName && utmFallbackBaseUrl) {
                        await safeTrackInteraction(
                          'homepage_link',
                          utmSource, // Use utmSource as UPRN (since location is disabled, we don't have actual UPRN)
                          utmFallbackAgencyName,
                          utmFallbackBaseUrl,
                          `Agent: ${utmFallbackAgencyName}`,
                          null,
                          null
                        );
                      }
                      // Navigate to the website after tracking completes
                      if (utmFallbackUrl) {
                        window.location.href = utmFallbackUrl;
                      }
                    }}
                    className="px-4 py-3 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors w-full max-w-md"
                  >
                    Link to our website
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Nearby Properties (hidden during UTM multi-match picker) */}
        {location && !locationLoading && !servicesDisabled && !showMultipleMatches && (
          <div className="bg-white shadow rounded-lg p-8">
            {/* UTM Source Display */}
            {utmSource && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Source:</span> {utmSource}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <HomeIcon className="h-8 w-8 text-indigo-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Nearby Properties</h2>
              </div>
              <button
                onClick={handleManualRefreshNearby}
                disabled={locationLoading || loadingProperties}
                className="p-2 bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 disabled:opacity-50 transition-colors"
                title="Refresh nearby properties"
              >
                <ArrowPathIcon className={`h-5 w-5 ${locationLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            {loadingProperties && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
                <span className="text-lg text-gray-600 mb-2">{loadingProgress.stage}</span>
                {loadingProgress.progress > 0 && (
                  <div className="w-full max-w-xs bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${loadingProgress.progress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            )}
            
            {filteringProperties && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3"></div>
                <span className="text-lg text-gray-600">Finding properties for {utmSource}...</span>
              </div>
            )}

            {utmSource && utmAwaitingAutoRoute && utmRedirectStatus && !loadingProperties && !filteringProperties && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3"></div>
                <span className="text-lg text-gray-600">
                  {utmRedirectStatus.kind === 'homepage'
                    ? `Opening homepage at ${utmRedirectStatus.url}...`
                    : `Opening listing at ${utmRedirectStatus.url}...`}
                </span>
              </div>
            )}
            
            {propertiesError && (
              <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-lg text-red-800">{propertiesError}</p>
              </div>
            )}
            
            {properties && properties.length > 0 && !loadingProperties && !filteringProperties
              && (!utmSource || !utmAwaitingAutoRoute) && (
              <div>
                {filteredProperties && filteredProperties.length === 1 && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-green-800">
                          <span className="font-medium">Showing properties for {utmSource}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {filteredProperties && filteredProperties.length === 0 && (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-800">
                          <span className="font-medium">No properties available for {utmSource}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(filteredProperties || properties).map((property, index) => (
                    <div 
                      key={index} 
                      className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow cursor-pointer active:bg-gray-100 touch-manipulation"
                      onClick={() => {
                        if (propertyRecords[property.uprn]?.agentListingLink) {
                          // Mark this property as clicked (single selection only)
                          setClickedProperties(new Set([property.uprn]));
                          
                          // Append UTM source to the agent listing URL
                          const baseUrl = propertyRecords[property.uprn].agentListingLink;
                          const separator = baseUrl.includes('?') ? '&' : '?';
                          const utmUrl = utmSource 
                            ? `${baseUrl}${separator}utm_source=qr.properties`
                            : `${baseUrl}${separator}utm_source=qr.properties`;
                          
                          // Track the click BEFORE navigation
                          const address = property.single_line_address || property.address || propertyRecords[property.uprn].addressFirstLine || `UPRN: ${property.uprn}`;
                          safeTrackInteraction('agent_listing_click', property.uprn, propertyRecords[property.uprn].agent, propertyRecords[property.uprn].agentListingLink, address, propertyRecords[property.uprn].propertyId, propertyRecords[property.uprn].branch);
                          
                          // Navigate to the agent listing URL
                          window.location.href = utmUrl;
                        }
                      }}
                      onTouchStart={() => {}} // Ensure touch events are captured
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (propertyRecords[property.uprn]?.agentListingLink) {
                            // Mark this property as clicked (single selection only)
                            setClickedProperties(new Set([property.uprn]));
                            
                            const baseUrl = propertyRecords[property.uprn].agentListingLink;
                            const separator = baseUrl.includes('?') ? '&' : '?';
                            const utmUrl = utmSource 
                              ? `${baseUrl}${separator}utm_source=qr.properties`
                              : `${baseUrl}${separator}utm_source=qr.properties`;
                            
                            // Track the click BEFORE navigation
                            const address = property.single_line_address || property.address || propertyRecords[property.uprn].addressFirstLine || `UPRN: ${property.uprn}`;
                            safeTrackInteraction('agent_listing_click', property.uprn, propertyRecords[property.uprn].agent, propertyRecords[property.uprn].agentListingLink, address, propertyRecords[property.uprn].propertyId);
                            
                            // Navigate to the agent listing URL
                            window.location.href = utmUrl;
                          }
                        }
                      }}
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {property.single_line_address || property.address || ''}
                      </h3>
                      {property.uprn && (
                        <p className="text-xs text-gray-500 mb-2">
                          UPRN: {property.uprn}
                        </p>
                      )}
                      {property.price && (
                        <p className="text-lg font-bold text-indigo-600 mb-2">
                          £{property.price.toLocaleString()}
                        </p>
                      )}
                      {property.description && (
                        <p className="text-sm text-gray-500 mb-2">{property.description}</p>
                      )}
                      {property.bedrooms && (
                        <p className="text-sm text-gray-600">Bedrooms: {property.bedrooms}</p>
                      )}
                      {property.bathrooms && (
                        <p className="text-sm text-gray-600">Bathrooms: {property.bathrooms}</p>
                      )}
                      {property.distance_m && (
                        <p className="text-sm text-gray-600 mt-2">
                          Distance: {Math.round(property.distance_m)}m
                        </p>
                      )}
                      
                      {/* Agent Listing Link Button - Only show if there's a property record with agent listing link */}
                      {property.uprn && propertyRecords[property.uprn] && propertyRecords[property.uprn].agentListingLink && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div>
                            {propertyRecords[property.uprn].agent && (
                              <p className="text-sm text-gray-600 mb-2">
                                Agent: <span className="font-medium">{propertyRecords[property.uprn].agent}</span>
                              </p>
                            )}
                            <div className="w-full flex items-center justify-center px-3 py-2 bg-green-600 text-white text-sm rounded-md">
                              {clickedProperties.has(property.uprn) ? (
                                <CheckIcon className="h-4 w-4 mr-2 text-white" />
                              ) : (
                                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                              )}
                              View Agent Listing
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Show "No Listing Available" only if we've confirmed there's no record */}
                      {property.uprn && propertyRecords[property.uprn] === null && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="w-full px-3 py-2 bg-gray-300 text-gray-600 text-sm rounded-md">
                            No Listing Available
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {properties && properties.length === 0 && !loadingProperties && !propertiesError
              && (!utmSource || !utmAwaitingAutoRoute) && (
              <div className="text-center py-12">
                <HomeIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg text-gray-600">No nearby properties found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Home;
